import { useState, useEffect } from 'react';
import { ArrowRight, ChevronRight, Star, Crown, Shield, Award } from "lucide-react";
// import backgroundImage from "../assets/home-background.jpg";
import backgroundImage from "../assets/background-image.jpg";

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, Building, Trophy } from "lucide-react";
import mentee4 from "../assets/mentee4.webp";
import mentee1 from "../assets/mentee1.jpg";


const Home = () => {
   // eslint-disable-next-line
  const [hoveredCard, setHoveredCard] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  
  // Track scroll position for parallax effects
  useEffect(() => {
    const handleScroll = () => setScrollPosition(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated number counter
  const [count, setCount] = useState({ members: 0, programs: 0, success: 0 });
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCount(prev => ({
        members: prev.members < 100 ? prev.members + 5 : 100,
        programs: prev.programs < 3 ? prev.programs + 1 : 3,
        success: prev.success < 95 ? prev.success + 2 : 95
      }));
    }, 100);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-gray-100 text-gray-900 overflow-hidden">
      {/* Hero Section with Enhanced Animation */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-green-800 via-green-700 to-green-900 text-white text-center py-20 px-6 overflow-hidden">
  {/* Add padding-top to account for navbar */}
  <div className="pt-16 md:pt-24 w-full">
    {/* Animated background elements */}
    <div className="absolute inset-0 overflow-hidden">
      <div 
        className="absolute inset-0 opacity-30 bg-cover bg-center transform scale-110 transition-transform duration-3000"
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          transform: `scale(1.1) translateY(-${scrollPosition * 0.05}px)` 
        }}
      />
      
      {/* Floating elements */}
      {[...Array(20)].map((_, i) => (
        <div 
          key={i}
          className="absolute rounded-full bg-white opacity-10"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            width: `${Math.random() * 50 + 10}px`,
            height: `${Math.random() * 50 + 10}px`,
            animation: `float ${Math.random() * 10 + 15}s linear infinite`,
            animationDelay: `${Math.random() * 5}s`
          }}
        />
      ))}
    </div>
    
    {/* Hero content with enhanced animations and better spacing */}
    <div className="relative max-w-5xl mx-auto z-10">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="rounded-full bg-white/10 backdrop-blur-sm px-6 py-2 mb-8 inline-block"
      >
        <span className="text-yellow-300 font-semibold">Transforming Potential into Excellence</span>
      </motion.div>
      
      <h1 className="text-4xl md:text-7xl font-extrabold mb-6 leading-tight">
        <motion.span 
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-white to-green-200"
        >
          Unlock
        </motion.span>{' '}
        <motion.span 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="inline-block"
        >
          Your
        </motion.span>{' '}
        <motion.span 
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="inline-block"
        >
          Leadership
        </motion.span>
        <motion.span 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="block text-yellow-400 mt-2 text-5xl md:text-8xl font-black"
        >
          Potential
        </motion.span>
      </h1>
      
      <motion.p 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.1 }}
        className="mt-6 text-xl md:text-2xl max-w-3xl mx-auto font-light"
      >
        Empowering the next generation of leaders through structured mentorship, 
        innovative talent management, and transformative leadership development.
      </motion.p>
      
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.3 }}
        className="mt-10 flex flex-col sm:flex-row justify-center gap-6"
      >
        <motion.a 
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.98 }}
          href="/membership" 
          className="group bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-10 py-5 rounded-full font-bold hover:from-yellow-400 hover:to-yellow-500 shadow-lg flex items-center justify-center gap-3 transition-all"
        >
          Join as Mentee
          <ArrowRight className="group-hover:translate-x-2 transition-transform" />
        </motion.a>
        
        <motion.a 
          whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)" }}
          whileTap={{ scale: 0.98 }}
          href="/membership" 
          className="group bg-white/10 backdrop-blur-sm text-white hover:text-green-100 border-2 border-white/20 px-10 py-5 rounded-full font-bold hover:bg-white/20 shadow-lg flex items-center justify-center gap-3 transition-all"
        >
          Become a Partner
          <ArrowRight className="group-hover:translate-x-2 transition-transform" />
        </motion.a>
      </motion.div>
      
      {/* Fixed spacing for the stats section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.8 }}
        className="relative mt-20 mb-16 mx-auto w-full max-w-4xl"
      >
        <div className="hidden md:flex justify-around p-6 bg-white rounded-2xl shadow-2xl">
          {[
            { value: count.members, label: "Active Members", symbol: "+" },
            { value: count.programs, label: "Programs Offered", symbol: "+" },
            { value: count.success, label: "Success Rate", symbol: "%" }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl font-extrabold text-green-700">
                {stat.value}{stat.symbol}
              </div>
              <div className="text-gray-500 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>

  {/* Custom floating animation */}
  <style jsx>{`
    @keyframes float {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      25% { transform: translateY(-20px) rotate(5deg); }
      50% { transform: translateY(-35px) rotate(0deg); }
      75% { transform: translateY(-15px) rotate(-5deg); }
    }
  `}</style>
</section>

      {/* Introduction Section with Gradient Card */}
      <section className="container mx-auto py-16 px-4 md:py-24 md:px-6 text-center relative mt-12 md:mt-16">
  {/* Animated Blobs */}
  <div className="absolute left-0 top-0 w-32 h-32 md:w-48 md:h-48 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
  <div className="absolute right-0 bottom-0 w-32 h-32 md:w-48 md:h-48 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
  
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="max-w-2xl md:max-w-3xl mx-auto"
  >
    {/* Heading */}
    <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">
      <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-600 to-green-800">
        Who We Are
      </span>
    </h2>

    {/* Content Box */}
    <div className="bg-white p-5 md:p-8 rounded-3xl shadow-lg relative z-10 border border-gray-200">
      <p className="text-base md:text-lg text-gray-700 leading-relaxed">
        <span className="font-semibold text-green-700">Led2Lead Consulting Limited</span> is a forward-thinking consulting firm committed to empowering individuals and organizations to reach their highest potentials. We specialize in expert mentoring, talent management, and leadership development solutions tailored to client needs.
      </p>

      {/* Button */}
      <motion.button
        className="mt-5 md:mt-6 bg-gradient-to-r from-green-600 to-green-800 text-white px-6 py-3 md:px-7 md:py-3.5 rounded-full font-bold shadow-md hover:shadow-lg transition-all"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
      >
        <Link to="/About" className="flex items-center gap-2">
          Learn More
          <ArrowRight className="w-5 h-5" />
        </Link>
      </motion.button>
    </div>
  </motion.div>

  {/* Blob Animation */}
  <style jsx>{`
    @keyframes blob {
      0%, 100% { transform: translate(0, 0) scale(1); }
      25% { transform: translate(10px, 10px) scale(1.05); }
      50% { transform: translate(0, 20px) scale(1); }
      75% { transform: translate(-10px, 10px) scale(0.95); }
    }
    
    .animate-blob {
      animation: blob 8s infinite;
    }
    
    .animation-delay-2000 {
      animation-delay: 1.5s;
    }
  `}</style>
</section>


      {/* Membership Categories with 3D Card Effects */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-green-100 skew-y-3 -z-10 transform -translate-y-20"></div>
        
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-800 font-medium text-sm mb-4">
              EXCLUSIVE OPPORTUNITIES
            </span>
            <h2 className="text-5xl font-bold text-green-800 mb-8">Membership Categories</h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Join our vibrant community and unlock exclusive benefits tailored to your career stage and aspirations.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Aspire Membership",
                description: "Aspire Members are the foundation of our community—long-term supporters and well-wishers who believe in our mission.",
                icon: <Shield className="w-16 h-16" />,
                color: "from-white to-gray-50",
                benefits: ["Access to community events", "Monthly newsletters", "Network with like-minded professionals"]
              },
              {
                title: "Empowered Membership",
                description: "Empowered Members are individuals who have been mentored and are thriving in their respective fields.",
                icon: <Crown className="w-16 h-16" />,
                color: "from-green-50 to-green-100",
                featured: true,
                benefits: ["One-on-one mentorship", "Weekly coaching sessions", "Career advancement resources", "Priority event access"]
              },
              {
                title: "Premium Membership",
                description: "Premium Members are our esteemed collaborators, sponsors, consultants, patrons, and advisors.",
                icon: <Award className="w-16 h-16" />,
                color: "from-white to-gray-50",
                benefits: ["Executive mentorship", "Business partnership opportunities", "Advisory board participation", "VIP industry connections"]
              }
            ].map((tier, index) => (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                key={index}
                className={`group perspective ${tier.featured ? 'md:-mt-8 md:mb-8 z-10' : ''}`}
              >
                <div className={`
                  relative p-8 rounded-2xl 
                  ${tier.featured ? 
                    'bg-gradient-to-br from-green-600 to-green-800 text-white shadow-2xl border-2 border-green-500' : 
                    `bg-gradient-to-br ${tier.color} shadow-xl border border-gray-100`
                  }
                  group-hover:shadow-2xl transition-all duration-300 h-full flex flex-col
                  transform group-hover:rotate-y-10 group-hover:scale-105
                `}>
                  {tier.featured && (
                    <div className="absolute -top-5 left-0 right-0 mx-auto w-max px-4 py-1 bg-yellow-400 text-green-900 font-bold rounded-full text-sm shadow-lg transform -rotate-2">
                      MOST POPULAR
                    </div>
                  )}
                  
                  <div>
                    <div className={`
                      ${tier.featured ? 'text-yellow-400' : 'text-green-700'} 
                      mb-6 transform group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500 flex justify-center
                    `}>
                      {tier.icon}
                    </div>
                    
                    <h3 className={`
                      text-2xl font-bold mb-4 text-center
                      ${tier.featured ? 'text-white' : 'text-green-700'}
                    `}>
                      {tier.title}
                    </h3>
                    
                    <p className={`
                      mb-8
                      ${tier.featured ? 'text-green-50' : 'text-gray-700'}
                    `}>
                      {tier.description}
                    </p>
                    
                    <ul className="space-y-3 mb-8">
                      {tier.benefits.map((benefit, i) => (
                        <li key={i} className={`
                          flex items-center
                          ${tier.featured ? 'text-green-50' : 'text-gray-700'}
                        `}>
                          <ChevronRight className={`
                            w-5 h-5 mr-2 flex-shrink-0
                            ${tier.featured ? 'text-yellow-400' : 'text-green-600'}
                          `} />
                          {benefit}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-auto pt-4">
                    <a
                      href="/membership"
                      className={`
                        inline-flex items-center justify-center gap-2 font-medium w-full py-3 px-4 rounded-xl
                        transition-all duration-300 
                        ${tier.featured ? 
                          'bg-white text-green-700 hover:bg-green-50' : 
                          'bg-green-700 text-white hover:bg-green-600'
                        }
                      `}
                    >
                      Learn More
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* 3D perspective styles */}
        <style jsx>{`
          .perspective {
            perspective: 1000px;
          }
          
          .rotate-y-10 {
            transform: rotateY(5deg);
          }
        `}</style>
      </section>

      {/* Programs Overview with Enhanced Cards */}
      <section className="bg-white py-16 px-6">
  <div className="container mx-auto">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="text-center mb-12 md:mb-16"
    >
      <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-800 font-medium text-sm mb-4">
        TRANSFORMATIVE SOLUTIONS
      </span>
      <h2 className="text-4xl md:text-5xl font-bold text-green-800 mb-6 md:mb-8">Our Programs</h2>
      <p className="max-w-2xl mx-auto text-base md:text-lg text-gray-600">
        Discover our carefully designed programs that empower individuals and organizations to thrive.
      </p>
    </motion.div>

    {/* Responsive Grid Layout */}
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[
        {
          title: "Mentorship Program",
          description:
            "Our mentorship initiative connects aspiring professionals with experienced mentors who provide guidance, career coaching, and personal development strategies.",
          icon: <GraduationCap className="w-12 h-12 md:w-14 md:h-14" />,
          bgImage:
            "url('https://images.unsplash.com/photo-1544377193-33dcf4d68fb5?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')",
        },
        {
          title: "Talent Management Program",
          description:
            "We identify and nurture high-potential individuals by equipping them with the skills needed to excel in their fields.",
          icon: <Building className="w-12 h-12 md:w-14 md:h-14" />,
          bgImage:
            "url('https://images.unsplash.com/photo-1577412647305-991150c7d163?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')",
        },
        {
          title: "Leadership Development Program",
          description:
            "Our leadership program is designed for individuals looking to enhance their leadership capabilities and make a lasting impact in their communities and organizations.",
          icon: <Trophy className="w-12 h-12 md:w-14 md:h-14" />,
          bgImage:
            "url('https://images.unsplash.com/photo-1559523182-a284c3fb7cff?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80')",
        },
      ].map((program, index) => (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: index * 0.2 }}
          key={index}
          className="group relative overflow-hidden rounded-3xl shadow-lg flex flex-col min-h-80 md:min-h-[24rem]"
        >
          {/* Card Background with Overlay */}
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
            style={{ backgroundImage: program.bgImage }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-green-900/70 to-green-900/90"></div>

          {/* Content */}
          <div className="relative h-full p-6 md:p-8 flex flex-col justify-between z-10 text-white">
            <div className="flex-grow">
              <div className="text-yellow-400 mb-3 transform group-hover:scale-110 transition-transform duration-500">
                {program.icon}
              </div>
              <h3 className="text-xl font-bold mb-2 md:mb-3">{program.title}</h3>
              <p className="opacity-85 group-hover:opacity-100 transition-opacity text-sm md:text-base">
                {program.description}
              </p>
            </div>

            {/* Button Always Aligned at Bottom */}
            <div className="mt-auto pt-3">
              <a
                href="/programs"
                className="inline-flex items-center gap-2 text-yellow-300 font-medium group-hover:text-yellow-200 transition-colors"
              >
                Learn More
                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </a>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </div>
</section>


      {/* Success Stories with Enhanced Design */}
      <section className="py-24 px-6 bg-gradient-to-br from-green-50 to-green-100 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-50">
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-white to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-64 bg-gradient-to-t from-white to-transparent"></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-green-100 text-green-800 font-medium text-sm mb-4">
              REAL RESULTS
            </span>
            <h2 className="text-5xl font-bold text-green-800 mb-8">Impact Stories</h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Discover how our programs have transformed careers and created lasting change.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {[
              {
                quote: "Starting Orbs Technologies was a challenging journey, but mentorship provided the clarity and strategic direction needed to navigate the business world. With guidance on structuring the company, securing clients, and managing a team, I built a sustainable and thriving business.",
                author: "Chukwunenye Nelson",
                role: "CEO, Orbs Technologies Limited",
                image: mentee4
              },
              {
                quote: "Mentorship has been instrumental in my journey as an accountant, providing guidance, encouragement, and support at every stage. Your wisdom and strength inspired me to push beyond my limits, achieve professional certifications, and build a successful career rooted in integrity and resilience.",
                author: "Adeola Sarah Adebisi",
                role: "Chartered Accountant",
                image: mentee1
              }
            ].map((story, index) => (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                key={index}
                className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 relative"
              >
                <div className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 text-green-300 opacity-50">
                  <svg width="120" height="120" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.5 8C9.5 8.44 9.35 8.89 9.06 9.24C8.76 9.59 8.34 9.83 7.88 9.9C8.36 10.06 8.76 10.36 9.04 10.74C9.31 11.13 9.48 11.55 9.56 12C9.64 12.45 9.52 12.91 9.24 13.3C8.95 13.69 8.52 13.97 8.04 14.1L6.6 14.5L7.27 10.4H3L2 16H4.05L4.56 13.46L6.89 14.11C7.72 14.34 8.53 14.24 9.3 13.83C10.07 13.42 10.6 12.75 10.9 11.82C11.2 10.89 11.15 10.01 10.75 9.17C10.35 8.32 9.66 7.77 8.7 7.5H10.38L10.5 7C10.59 6.67 10.59 6.33 10.5 6C10.41 5.67 10.23 5.38 10 5.15C9.76 4.91 9.48 4.72 9.15 4.63C8.82 4.54 8.48 4.54 8.15 4.63C7.82 4.72 7.54 4.91 7.3 5.15C7.06 5.38 6.88 5.67 6.79 6C6.7 6.33 6.7 6.67 6.79 7L7 8H9.5Z" fill="currentColor"/>
                    <path d="M19.5 8C19.5 8.44 19.35 8.89 19.06 9.24C18.76 9.59 18.34 9.83 17.88 9.9C18.36 10.06 18.76 10.36 19.04 10.74C19.31 11.13 19.48 11.55 19.56 12C19.64 12.45 19.52 12.91 19.24 13.3C18.95 13.69 18.52 13.97 18.04 14.1L16.6 14.5L17.27 10.4H13L12 16H14.05L14.56 13.46L16.89 14.11C17.72 14.34 18.53 14.24 19.3 13.83C20.07 13.42 20.6 12.75 20.9 11.82C21.2 10.89 21.15 10.01 20.75 9.17C20.35 8.32 19.66 7.77 18.7 7.5H20.38L20.5 7C20.59 6.67 20.59 6.33 20.5 6C20.41 5.67 20.23 5.38 20 5.15C19.76 4.91 19.48 4.72 19.15 4.63C18.82 4.54 18.48 4.54 18.15 4.63C17.82 4.72 17.54 4.91 17.3 5.15C17.06 5.38 16.88 5.67 16.79 6C16.7 6.33 16.7 6.67 16.79 7L17 8H19.5Z" fill="currentColor"/>
                  </svg>
                </div>
                
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-full overflow-hidden shadow-lg border-2 border-green-500">
                      <img 
                        src={story.image} 
                        alt={story.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  <div>
                  <div className="text-yellow-500 mb-4 flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic relative z-10">{story.quote}</p>
                <div className="text-green-700 font-bold text-lg">{story.author}</div>
                <div className="text-gray-600">{story.role}</div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>

  {/* Call to Action Section - NEW */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-emerald-800 text-white relative overflow-hidden">
  {/* Background Pattern */}
  <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIxIiBvcGFjaXR5PSIwLjEiLz48L2c+PC9zdmc+')] opacity-20"></div>

  {/* Content */}
  <motion.div 
    className="container mx-auto max-w-4xl text-center px-6 md:px-8 relative"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.8 }}
  >
    <h2 className="text-3xl md:text-5xl font-bold mb-6">
      Ready to Begin Your Mentorship Journey?
    </h2>
    <p className="text-lg md:text-xl text-green-100 mb-10 max-w-3xl mx-auto">
      Join our community of emerging leaders and experienced mentors. 
      Take the first step towards transforming your potential into lasting impact.
    </p>

    {/* Buttons */}
    <div className="flex flex-col md:flex-row gap-4 justify-center">
      
      {/* Book a Consultation Button */}
      <Link to="/contact">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="w-full md:w-auto px-8 py-4 bg-white text-green-700 rounded-full font-bold text-lg shadow-lg hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
        >
          Start your Journey
          <motion.span
            animate={{ x: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            →
          </motion.span>
        </motion.button>
      </Link>

      {/* Explore Our Programs Button (Fixed) */}
      <Link to="/programs">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="w-full md:w-auto px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
        >
          Explore Our Programs
        </motion.button>
      </Link>

    </div>
  </motion.div>
</section>
</div>
);
};

export default Home;